import { LegalPageLayout, Section } from "../components/legal/LegalPageLayout";
import { Link } from "react-router-dom";

function DateCallout({ date, text }: { date: string; text: string }) {
  return (
    <div
      className="my-4 rounded-lg border-l-4 p-4"
      style={{
        borderColor: "var(--color-primary)",
        background: "var(--color-surface-secondary)",
      }}
    >
      <p className="font-semibold text-[var(--color-text-primary)]">{date}</p>
      <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{text}</p>
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex gap-2">
      <span className="mt-0.5 h-1.5 w-1.5 shrink-0 translate-y-1.5 rounded-full bg-[var(--color-primary)]" />
      <span>{children}</span>
    </li>
  );
}

export function BlogAfirComplianceGuide2026() {
  return (
    <LegalPageLayout
      title="AFIR Compliance Software 2026: Praxisleitfaden für Charge Point Operators"
      lastUpdated="30. April 2026"
      crossLink={{ to: "/blog", label: "Zurück zur Übersicht" }}
      contactEmail="contact@chargesuite.de"
    >
      {/* Lede — €100K fine */}
      <Section
        id="lede-bussgeld"
        title="100.000 Euro pro Verstoß: Warum AFIR Compliance Software 2026 Pflicht wird"
      >
        <p>
          Am 25. März 2026 hat das Bundeskabinett einen Gesetzentwurf zur
          Änderung des Preisangabenrechts beschlossen, der den Bußgeldrahmen
          für Verstöße gegen die Preisangabepflichten an Ladesäulen von
          bisher 25.000 Euro auf <strong>bis zu 100.000 Euro</strong> pro
          Verstoß anhebt. Verbatim aus der Gesetzesbegründung des
          Bundesministeriums für Wirtschaft: <em>&bdquo;Die Erhöhung stellt
          einen angemessenen Bußgeldrahmen für die Ahndung von Verstößen
          gegen die Regelungen zu Preisangaben in der AFIR und der PAngV
          sicher.&ldquo;</em> Das Gesetz muss noch durch Bundestag und Bundesrat
          &mdash; doch der Marschbefehl ist eindeutig: Die Bundesnetzagentur
          (BNetzA) bekommt 2026 die Sanktionsbefugnis, die ihr für die
          Durchsetzung der AFIR bisher fehlte.
        </p>
        <p>
          Bis zu diesem Zeitpunkt hatte die deutsche Aufsicht eine ehrliche
          Lücke: Die Alternative Fuels Infrastructure Regulation (AFIR,
          Verordnung (EU) 2023/1804) trat am 13. April 2024 mit ihren
          materiellen Pflichten in Kraft, aber die Ladesäulenverordnung in der
          Fassung von Dezember 2024 enthielt keinen eigenständigen
          Bußgeldrahmen für Preisangabenverstöße. CPOs konnten also
          unzureichende Preisanzeigen, fehlende Ad-hoc-Zahlung oder
          minutenbasierte Tarife technisch nicht-konform betreiben &mdash; die
          BNetzA hatte schlicht kein Schwert. Diese Phase endet 2026.
          Gleichzeitig wird ab 14. April 2026 die Datenmeldepflicht nach
          Artikel 20 AFIR verbindlich im DATEX-II-Format gegenüber der
          Mobilithek fällig. Wer jetzt seine Compliance-Infrastruktur nicht
          aufrüstet, hat ein gestapeltes Risiko: Bußgeld + zivilrechtliche
          Forderungsangriffe + DSO-Sperren bei nicht gemeldeten Daten.
        </p>
        <p>
          Dieser Leitfaden beschreibt, was eine AFIR Compliance Software 2026
          konkret leisten muss, welche Artikel der AFIR welche
          Software-Funktionen erzwingen, und wie deutsche CPOs Eichrecht und
          AFIR architekturkonform zusammenführen. Grundlage sind die
          Originaltexte der Verordnung (EU) 2023/1804, das deutsche
          Messgerätegesetz, der LSV-Referentenentwurf 2026 sowie publizierte
          Anforderungen der Nationalen Leitstelle Ladeinfrastruktur.
        </p>

        <DateCallout
          date="25. März 2026"
          text="Bundeskabinett beschließt Anhebung des PAngV-Bußgeldrahmens auf 100.000 Euro für Verstöße gegen AFIR-Preisangabepflichten. BNetzA wird zuständige Aufsichtsbehörde."
        />
      </Section>

      {/* Article 5 — Ad-hoc + kWh-primary */}
      <Section
        id="art-5-ad-hoc-kwh"
        title="Artikel 5 AFIR: Ad-hoc-Zahlung und kWh-Primärbasierung"
      >
        <p>
          Artikel 5 Absatz 1 der AFIR ist seit dem 13. April 2024 verbindlich:{" "}
          <em>&bdquo;Recharging on an ad hoc basis at every recharging point
          operated by him… without being required to register, conclude a
          written agreement or enter into a commercial relationship with the
          operator… and without the need to enter into a contract with a
          mobility service provider.&ldquo;</em> Praktisch bedeutet das: An
          jedem öffentlich zugänglichen Ladepunkt muss ein Nutzer ohne
          Vorregistrierung, ohne Kundenvertrag, ohne App und ohne
          Roaming-Zugehörigkeit laden und bezahlen können. Reine
          App-only-Lösungen erfüllen die AFIR nicht. RFID-Karten allein
          erfüllen sie nicht. Bargeldzahlung erfüllt sie nicht.
        </p>
        <p>
          Artikel 5 Absatz 4 verlangt für alle DC-Ladepunkte ab 50 kW, dass
          der Ad-hoc-Preis primär auf dem Preis pro Kilowattstunde basiert.
          Zeitbasierte Komponenten sind nur als <em>ergänzendes
          Belegungsentgelt</em> gegen Dauerblockaden zulässig &mdash; nicht
          als primäre Abrechnungsgröße. Diese Vorgabe konvergiert mit dem
          deutschen Eichrecht, das ohnehin geeichte kWh-Messung verlangt, aber
          sie schafft eine eigenständige Sanktionsspur: Wer minutenbasiert
          abrechnet oder zeitbasierte Komponenten als Hauptpreis vermarktet,
          riskiert ab 2026 zusätzlich zum Eichrechts-Bußgeld nach § 60 MessEG
          die neue PAngV-Sanktion bis 100.000 Euro.
        </p>
        <p>
          Eine AFIR Compliance Software muss diese Tarifregeln strukturell
          erzwingen: Tarifeditoren, die kWh-Tarife als Pflichtfeld behandeln
          und zeitbasierte Komponenten nur als sekundäres Element zulassen,
          mit Plausibilitätsprüfung, dass der kWh-Preis dominant bleibt.
          Software, die diese Trennung dem Operator als optionale
          Konfiguration überlässt, ist 2026 ein Compliance-Risiko.
        </p>
      </Section>

      {/* Article 5(5) — Card terminal + Article 7 */}
      <Section
        id="art-5-7-zahlung-transparenz"
        title="Artikel 5 Absatz 5 und Artikel 7: Kartenzahlung und Preistransparenz"
      >
        <p>
          Artikel 5 Absatz 5 AFIR verlangt für alle nach dem 13. April 2024
          neu errichteten öffentlich zugänglichen Ladepunkte ab 50 kW
          mindestens eine der folgenden Zahlungsmöglichkeiten: ein
          Kartenleser für Zahlungskarten oder ein kontaktloses Lesegerät, das
          Zahlungskarten bzw. NFC-fähige Wallets akzeptiert. Statische
          QR-Codes genügen nur bei langsameren Stationen unter 50 kW. Für
          Bestandsanlagen über 150 kW gilt eine Nachrüstpflicht zum 1. April
          2026 nach Vorgabe der Nationalen Leitstelle Ladeinfrastruktur.
        </p>
        <p>
          Artikel 7 AFIR verschärft das Transparenzgebot: Vor jedem
          Ladevorgang muss der Gesamtpreis in seinen Bestandteilen sichtbar
          sein &mdash; kWh-Preis, etwaige Belegungsgebühren, Sitzungsgebühren
          und Steuern getrennt ausgewiesen. Eine reine App-Anzeige genügt
          nicht; der Preis muss am physischen Terminal oder Stationsdisplay
          erscheinen. Ändert sich der Preis während der Sitzung &mdash; etwa
          bei dynamischen Tarifen oder beim Wechsel zwischen Belastungsstufen
          &mdash; muss die Aktualisierung am Display sichtbar werden und der
          Nutzer informiert sein. Genau diese Pflichten werden ab 2026 mit dem
          neuen 100.000-Euro-Rahmen sanktionierbar.
        </p>
      </Section>

      {/* Article 20 — DATEX II / NAP */}
      <Section
        id="art-20-datex-mobilithek"
        title="Artikel 20 AFIR: DATEX II und Mobilithek-Pflicht ab 14. April 2026"
      >
        <p>
          Artikel 20 AFIR verlangt von jedem Betreiber öffentlich zugänglicher
          Ladepunkte, statische und dynamische Daten kostenlos und
          diskriminierungsfrei an den National Access Point (NAP) zu liefern.
          In Deutschland ist das die <strong>Mobilithek</strong>, betrieben
          vom Bundesministerium für Verkehr. Seit 14. April 2025 ist die
          Datenübermittlung verpflichtend; ab dem <strong>14. April
          2026</strong> muss das Format zwingend DATEX II in der von der NOW
          GmbH publizierten Profilversion v01-00-00 sein.
        </p>

        <DateCallout
          date="14. April 2026"
          text="DATEX-II-Profil v01-00-00 wird verpflichtend. Statische Daten innerhalb von 24 Stunden, dynamische Daten innerhalb von 1 Minute nach Änderung."
        />

        <p>
          Statische Daten umfassen Standort, Steckertypen, Betriebszeiten,
          Fahrzeugkompatibilität und Tarifstruktur &mdash; aktualisierbar
          innerhalb von 24 Stunden nach jeder Änderung. Dynamische Daten
          umfassen Verfügbarkeit, Betriebsstatus, Echtzeitpreis und
          Erneuerbare-Anteil &mdash; aktualisierbar innerhalb von 60 Sekunden
          nach jeder Statusänderung. Diese Frequenzen lassen sich nicht durch
          klassisches Polling abbilden; eine event-getriebene Architektur ist
          notwendig.
        </p>
        <p>
          Wichtig für deutsche CPOs: Die Mobilithek-Meldung ersetzt nicht das
          BNetzA-Ladesäulenregister. Die formellen Anzeigepflichten der
          Ladesäulenverordnung gegenüber der Bundesnetzagentur bestehen
          parallel weiter. Operator müssen also <em>zwei</em> Meldekanäle
          bedienen, deren Datensätze nur teilweise überlappen. Eine
          AFIR-Compliance-Software muss beide Kanäle aus einer Datenquelle
          versorgen können, sonst entstehen Inkonsistenzen, die in einer
          BNetzA-Prüfung sofort auffallen.
        </p>
        <p>
          Personenbezogene Daten &mdash; Fahrzeug-IDs, Zahlungsinformationen,
          eindeutige Sitzungs-IDs &mdash; dürfen nach Artikel 14 Absatz 7
          AFIR nicht über den NAP laufen. Die Datenpipeline muss daher eine
          klare Trennung zwischen öffentlichem Telematik-Stream und
          DSGVO-pflichtigen Sitzungs- und Abrechnungsdaten vornehmen.
        </p>
      </Section>

      {/* What AFIR compliance software does */}
      <Section
        id="was-software-leistet"
        title="Was AFIR Compliance Software konkret leisten muss"
      >
        <p>
          Eine für 2026 belastbare AFIR-Compliance-Lösung deckt sieben
          Funktionsbereiche ab. Wer beim Vendor-Vergleich Punkte streichen
          will, sollte das hier bewusst tun &mdash; nicht aus Versehen.
        </p>
        <ul className="ml-2 mt-3 space-y-2">
          <Bullet>
            <strong>Datenmeldung an die Mobilithek nativ</strong>: DATEX II
            v01-00-00 Export ohne Drittadapter. Statische Daten 24 h,
            dynamische Daten 1 min. Vertraglich zugesicherte SLA.
          </Bullet>
          <Bullet>
            <strong>Tarif-Engine mit kWh-Primärzwang</strong>: kWh-Preis als
            Pflichtfeld; zeitbasierte Komponenten nur als ergänzendes
            Belegungsentgelt; Plausibilitätsprüfung im Editor.
          </Bullet>
          <Bullet>
            <strong>Ad-hoc-Zahlungslogik</strong>: ohne Vorregistrierung,
            ohne App, ohne Roaming-Vertrag. Integration mit physischem
            Kartenterminal und kontaktlosem NFC-Reader.
          </Bullet>
          <Bullet>
            <strong>Eichrechtskonforme Signaturkette</strong>: OCMF-Datensätze
            werden vom CPMS unverändert weitergeleitet, nicht im Backend
            re-signiert. Prüfung der Signaturpfad-Integrität im
            Vendor-Whitepaper.
          </Bullet>
          <Bullet>
            <strong>BNetzA-Ladesäulenregister-Synchronisation</strong>:
            zweiter Meldekanal aus derselben Datenquelle, mit Diff-Logging
            zwischen den beiden Registern.
          </Bullet>
          <Bullet>
            <strong>Audit-Trail und Evidenzpaket</strong>: Export
            historischer Tarif-, Verfügbarkeits- und Sitzungsdaten in einer
            Form, die in einem behördlichen Prüfungstermin nachvollziehbar
            ist. Versionierung von Tarifänderungen mit Zeitstempel.
          </Bullet>
          <Bullet>
            <strong>Deadline-Tracking und Gap-Analyse</strong>: Software
            kennt die laufenden Fristen aus AFIR, LSV, EnWG, BDEW, MessEG, CRA
            und alarmiert den Operator vor Verstreichen. Eine reine
            Tabellenkalkulation reicht für sieben parallele Regelwerke nicht.
          </Bullet>
        </ul>
      </Section>

      {/* Enforcement reality */}
      <Section
        id="enforcement-2026"
        title="Durchsetzungslage 2026: Was Operator real erwartet"
      >
        <p>
          Bis April 2026 hat die BNetzA noch kein öffentlich bekanntes
          Bußgeld gegen einen CPO wegen AFIR-Preistransparenzverstoß
          verhängt. Das ist kein Hinweis auf Toleranz, sondern auf die
          beschriebene Sanktionslücke. Aktive Durchsetzung erfolgte bislang
          über Zivilrecht: Im Februar 2026 verhandelte das OLG München
          eine Klage gegen diskriminierende Ladepreise auf Basis des
          Wettbewerbsrechts (UWG § 3a in Verbindung mit der PAngV). Das
          Privatrechtsrisiko besteht also bereits heute.
        </p>
        <p>
          Mit Inkrafttreten der PAngV-Novelle &mdash; erwartet H2 2026 &mdash;
          beginnt die zweite Welle. Erfahrungen aus anderen Sanktionsregimen
          (DSGVO, Verbraucherinformationsrecht) zeigen ein typisches Muster:
          die ersten zwölf Monate sind <em>Mahn- und Schaufensterphase</em>
          mit publikumswirksamen Einzelfällen, dann setzt
          standardisierte Bußgeldpraxis ein. Wer 2026 noch nachrüstet, hat
          das Zeitfenster genutzt. Wer im H2 2027 anfängt, baut während einer
          aktiven Prüfkampagne um.
        </p>
        <p>
          Das parallele Eichrechtsregime liefert einen historischen Vergleich:
          Im August 2022 wurden über 1.800 Tesla Supercharger in Deutschland
          als nicht eichrechtskonform identifiziert. Tesla berichtet seither
          quartalsweise an das Landesamt für Mess- und Eichwesen
          Berlin-Brandenburg über den Umrüstungsfortschritt. Das Bußgeld
          wurde im konkreten Fall nicht maximal verhängt, aber das
          Reputations- und Aufsichtsrisiko war erheblich. Die AFIR-Aufsicht
          2026 wird diesen Pfad voraussichtlich nicht einfach wiederholen
          &mdash; sie startet mit fertigem Bußgeldrahmen.
        </p>
      </Section>

      {/* Snapshot + CTA */}
      <Section id="cta" title="Nächster Schritt: Compliance-Prüfung starten">
        <p>
          ChargeSuite kombiniert AFIR-Datenmeldung, Eichrechts-Signaturpfad,
          BNetzA-Synchronisation und Deadline-Tracking in einer Plattform, die
          über Ihre bestehenden CPMS- und Hardware-Stacks hinweg arbeitet.
          Unser Compliance Brain prüft 188 Einzelanforderungen aus sieben
          Regelwerken und meldet Sie fristgerecht &mdash; bevor Behörden es
          tun. Die Implementierung ersetzt kein bestehendes CPMS, sondern
          legt eine Compliance-Schicht darüber.
        </p>
        <p>
          Eine 30-minütige kostenlose Compliance-Prüfung zeigt, welche der
          über 120 Einzelpflichten Ihre aktuelle Infrastruktur heute schon
          erfüllt &mdash; und welche bis 14. April 2026 nachzubessern sind.
          Sie erhalten ein PDF-Evidenzpaket, das in einer
          BNetzA-Inspektion bestehen würde.
        </p>
        <p className="mt-4">
          <Link
            to="/audit"
            style={{ color: "var(--color-primary)" }}
            className="font-semibold hover:underline"
          >
            Jetzt kostenlose AFIR-Compliance-Prüfung starten &rarr;
          </Link>{" "}
          oder{" "}
          <Link
            to="/pricing"
            style={{ color: "var(--color-primary)" }}
            className="font-semibold hover:underline"
          >
            Preise und Pakete vergleichen
          </Link>
          .
        </p>
      </Section>
    </LegalPageLayout>
  );
}
